if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.initResponsiveDetails();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      initResponsiveDetails() {
        const info = this.querySelector('.product__info-container');
        if (!info) return;

        const details = this.querySelectorAll(
          'details[id^="Details-nutrition-"], details[id^="Details-specifications-"], details[id="Details-product-reviews"]'
        );
        this.responsiveDetails = Array.from(details, (detail) => {
          const element = detail.closest('.product__accordion');
          const anchor = document.createElement('template');
          element.before(anchor);
          return { element, anchor, smallHide: element.classList.contains('small-hide') };
        });
        if (!this.responsiveDetails.length) return;

        this.detailsMediaQuery = window.matchMedia('(max-width: 749px)');
        this.syncResponsiveDetails = () => {
          if (this.detailsMediaQuery.matches) {
            const existingAccordions = Array.from(info.querySelectorAll(':scope > .product__accordion')).filter(
              (element) => !this.responsiveDetails.some((detail) => detail.element === element)
            );
            let previous = existingAccordions.at(-1);
            for (const { element } of this.responsiveDetails) {
              element.classList.remove('small-hide');
              if (previous) {
                previous.after(element);
              } else {
                info.append(element);
              }
              previous = element;
            }
          } else {
            for (const { element, anchor, smallHide } of this.responsiveDetails) {
              anchor.after(element);
              element.classList.toggle('small-hide', smallHide);
            }
          }
        };
        this.detailsMediaQuery.addEventListener('change', this.syncResponsiveDetails);
        this.syncResponsiveDetails();
      }

      destroyResponsiveDetails() {
        this.detailsMediaQuery?.removeEventListener('change', this.syncResponsiveDetails);
        for (const { element, anchor, smallHide } of this.responsiveDetails || []) {
          anchor.after(element);
          element.classList.toggle('small-hide', smallHide);
          anchor.remove();
        }
        this.responsiveDetails = [];
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.destroyResponsiveDetails();
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');

          const inventoryEl = this.querySelector(`#Inventory-${this.dataset.section}`);
          
          if (variant && variant.inventory_management !== 'shopify') {
            inventoryEl?.classList.add('visibility-hidden');
          } else {
            inventoryEl?.classList.remove('visibility-hidden');
          }

          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.productForm?.toggleSubmitButton(
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
            window.variantStrings.soldOut
          );

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shop_url}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);
        const thumbnailsSource = this.querySelector('media-gallery [id^="GalleryThumbnails"] ul');
        const thumbnailsDestination = html.querySelector(`media-gallery [id^="GalleryThumbnails"] ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        // Update thumbnails to match the updated main gallery
        if (thumbnailsSource && thumbnailsDestination) {
          const thumbnailsDestinationItems = Array.from(
            thumbnailsDestination.querySelectorAll('li[data-target]')
          );
          const thumbnailsSourceItems = Array.from(
            thumbnailsSource.querySelectorAll('li[data-target]')
          );

          // Get updated media IDs from the main gallery
          const updatedMediaItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const updatedMediaIds = updatedMediaItems.map((item) => item.dataset.mediaId);

          // Create a map of media.id (last part after last dash) to mediaId for matching
          const mediaIdByMediaId = new Map();
          updatedMediaItems.forEach((item) => {
            const mediaIdParts = item.dataset.mediaId.split('-');
            const mediaId = mediaIdParts[mediaIdParts.length - 1];
            mediaIdByMediaId.set(mediaId, item.dataset.mediaId);
          });

          // Create a map of destination thumbnails by their target (for cloning)
          const destinationThumbnailMap = new Map();
          thumbnailsDestinationItems.forEach((item) => {
            const targetParts = item.dataset.target.split('-');
            const mediaId = targetParts[targetParts.length - 1];
            destinationThumbnailMap.set(mediaId, item);
          });

          // Remove thumbnails that don't have corresponding media items
          thumbnailsSourceItems.forEach((thumbnail) => {
            const targetParts = thumbnail.dataset.target.split('-');
            const mediaId = targetParts[targetParts.length - 1];
            if (!mediaIdByMediaId.has(mediaId)) {
              thumbnail.remove();
            }
          });

          // Update or add thumbnails to match the updated main gallery
          updatedMediaIds.forEach((mediaId, index) => {
            const mediaIdParts = mediaId.split('-');
            const mediaIdOnly = mediaIdParts[mediaIdParts.length - 1];
            
            // Find existing thumbnail by matching media.id (last part)
            const existingThumbnail = Array.from(thumbnailsSource.querySelectorAll('li[data-target]')).find(
              (thumb) => {
                const thumbTargetParts = thumb.dataset.target.split('-');
                return thumbTargetParts[thumbTargetParts.length - 1] === mediaIdOnly;
              }
            );

            if (existingThumbnail) {
              // Update data-target to match the new mediaId format
              if (existingThumbnail.dataset.target !== mediaId) {
                existingThumbnail.dataset.target = mediaId;
              }
              // Update position if needed
              const currentIndex = Array.from(thumbnailsSource.children).indexOf(existingThumbnail);
              if (currentIndex !== index) {
                const targetElement = thumbnailsSource.children[index];
                if (targetElement) {
                  thumbnailsSource.insertBefore(existingThumbnail, targetElement);
                } else {
                  thumbnailsSource.appendChild(existingThumbnail);
                }
              }
            } else {
              // Add new thumbnail from destination
              const destinationThumbnail = destinationThumbnailMap.get(mediaIdOnly);
              if (destinationThumbnail) {
                const newThumbnail = destinationThumbnail.cloneNode(true);
                newThumbnail.dataset.target = mediaId;
                const targetElement = thumbnailsSource.children[index];
                if (targetElement) {
                  thumbnailsSource.insertBefore(newThumbnail, targetElement);
                } else {
                  thumbnailsSource.appendChild(newThumbnail);
                }
              }
            }
          });
        }

        // set featured media as active in the media gallery
        // Find the correct mediaId from the updated gallery (may have different section prefix)
        const mediaGallery = this.querySelector(`media-gallery`);
        if (mediaGallery) {
          // Try to find the media item by matching the variantFeaturedMediaId (last part)
          const mediaIdParts = `${this.dataset.section}-${variantFeaturedMediaId}`.split('-');
          const mediaIdSuffix = mediaIdParts[mediaIdParts.length - 1];
          
          // Find the actual mediaId from the updated gallery
          const actualMediaItem = mediaGallerySource?.querySelector(`li[data-media-id$="-${mediaIdSuffix}"]`);
          const mediaIdToUse = actualMediaItem?.dataset.mediaId || `${this.dataset.section}-${variantFeaturedMediaId}`;
          
          mediaGallery.setActiveMedia?.(mediaIdToUse, true);
        }

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}
